import { useState, useEffect, useRef } from 'react'
import type { FilterOptions, GovdataResponse } from '../../types/govdata'

interface UseGovdataResult<T> {
  data: T[]
  filters: FilterOptions | null
  loading: boolean
  error: string | null
}

/**
 * Generic hook for fetching govdata API responses.
 *
 * Re-fetches whenever `paramsKey` changes (caller should JSON.stringify their params).
 * Handles race conditions via a generation counter so stale responses are ignored.
 */
export function useGovdata<T>(
  fetchFn: () => Promise<GovdataResponse<T>>,
  paramsKey: string,
): UseGovdataResult<T> {
  const [data, setData] = useState<T[]>([])
  const [filters, setFilters] = useState<FilterOptions | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const generation = useRef(0)

  useEffect(() => {
    const gen = ++generation.current
    setLoading(true)
    setError(null)

    fetchFn()
      .then((res) => {
        if (gen !== generation.current) return // stale
        setData(res.results)
        setFilters(res.filters)
        setLoading(false)
      })
      .catch((err) => {
        if (gen !== generation.current) return
        setError(err.message || 'Failed to load data')
        setLoading(false)
      })
  }, [paramsKey])

  return { data, filters, loading, error }
}
